import HIGHLIGHT_MODES from '@modules/JourneyBuilder/Components/Charts/SankeyV2/highlight-modes';
import {debounceWithLeadingCallAsync} from "@modules/JourneyBuilder/Components/Charts/SankeyV2/helpers";

import api, {controllerEndpoint} from "@modules/JourneyBuilder/api";
import sankeyApi from "@modules/JourneyBuilder/Components/Charts/sankey-api";

export default {
    namespaced: true,
    state: {
        /**
         * @type {String|undefined} 
         */
        journeyId: undefined,

        /**
         * @type {SankeyNode[]}
         */
        sankeyData: [],

        /**
         * @type {SankeyType[]}
         */
        sankeyTypes: [],

        /**
         * @type {String|undefined} Selected sankey brick id
         */
        selectedBrickId: undefined,

        /**
         * @type {Number|undefined}
         */
        periodId: null,

        /**
         * @type {Number|undefined}
         */
        inflowPeriodId: null,
        
        /**
         * @type {Array.<String>}
         */
        productNames: [],

        /**
         * @type {Boolean}
         */
        isLoadingPeriodRange: false,
        
        /**
         * @type {String|undefined}
         */
        periodStart: undefined,

        /**
         * @type {String|undefined}
         */
        periodEnd: undefined
    },
    getters: {
        //Hack; we only want to trigger our journey/periodId 
        //watches once per update cycle to prevent DDOSing ourselves/race conditions :-)
        watchableReportStatisticsParameters(state){
            const sortedProductNames = [...state.productNames]
                .sort((left, right) => left.localeCompare(right));

            return `${state.journeyId}|${state.periodId}|${state.inflowPeriodId}|${sortedProductNames.join('|')}`;
        },
        /**
         * Parameters to be used for reading sankey/report statistics widget data
         * @param state
         * @return {JourneyReportStatisticsParameters}
         */
        reportStatisticsParameters(state){
            return {
                periodId: state.periodId,
                inflowPeriodId: state.inflowPeriodId,
                productNames: state.productNames
            }
        },
        /**
         * Array containing all supported highlight modes based on the current sankey data
         * @param state
         * @return {String[]} supported highlight modes
         */
        supportedHighlightModes(state){
            const supportedHighlightModes = [];
            
            //Goal nodes with a total of 0 will skew results, thus we don't count those
            const hasGoalNodes = state.sankeyData.some(n => n.isGoal && n.total !== 0);
            
            //Not the most efficient solution, will suffice for now
            //Select all final (= no other node with from = node.id) non-root nodes (= node.from = truthy)
            const hasRegularEndNodes = state.sankeyData.some(n => n.from && !state.sankeyData.some(nP => nP.from === n.id));
            
            if(hasGoalNodes){
                supportedHighlightModes.push(HIGHLIGHT_MODES.LEAST_SUCCESSFUL_ROUTE, HIGHLIGHT_MODES.MOST_SUCCESSFUL_ROUTE);
            }
            
            if(hasRegularEndNodes){
                supportedHighlightModes.push(HIGHLIGHT_MODES.LEAST_TAKEN_ROUTE, HIGHLIGHT_MODES.MOST_TAKEN_ROUTE);
            }
            
            return supportedHighlightModes;
        },
        /**
         * The currently selected nodes
         * @param state
         * @return {SankeyNode|null}
         */
        selectedNodes(state){
            if(!state.sankeyData || !state.selectedBrickId){
                return [];
            }
            
            return state.sankeyData.filter(node => node.brickId === state.selectedBrickId);
        }
    },
    mutations: {
        setJourneyId(state, journeyId){
            state.journeyId = journeyId;
        },
        setSankeyData(state, sankeyData){
            state.sankeyData = sankeyData;
        },
        setSankeyTypes(state, sankeyTypes){
            state.sankeyTypes = sankeyTypes;
        },
        setSelectedBrickId(state, brickId){
            state.selectedBrickId = brickId;
        },
        setPeriodId(state, periodId){
            state.periodId = periodId;
        },
        setPeriodStart(state, start){
            state.periodStart = start;
        },
        setIsLoadingPeriodRange(state, value){
            state.isLoadingPeriodRange = value;
        },
        setPeriodEnd(state, end){
            state.periodEnd = end;
        },
        setInflowPeriodId(state, inflowPeriodId) {
            state.inflowPeriodId = inflowPeriodId;
        },
        setProductNames(state, productNames){
            state.productNames = productNames;
        }
    },
    actions: {
        loadSankeyData: debounceWithLeadingCallAsync(async ({state, getters, commit}) => {
            const remoteData = await sankeyApi.getJourneyDataFromEndpoint(
                `${controllerEndpoint}GetSankeyData`, state.journeyId, getters.reportStatisticsParameters
            );
            
            remoteData.types.sort(({name: left}, {name: right}) => {
                left = left.toLowerCase();
                right = right.toLowerCase();

                //Audience should always be first
                if(left === 'audience' || right === 'audience'){
                    return left === 'audience' ? -1 : 1;
                }

                //Sort the rest alphabetically
                return left.localeCompare(right);
            });
            
            commit('setSankeyData', remoteData.data);
            commit('setSankeyTypes', remoteData.types);
        }),
        loadPeriodDateRange: debounceWithLeadingCallAsync(async ({commit}, periodId) => {
            commit('setIsLoadingPeriodRange', true);
            
            try {
                const {start, end} = await api.getStatisticsPeriodDateRange(periodId);

                commit('setPeriodStart', start);
                commit('setPeriodEnd', end);
            } catch(e){
                //Reset our start/end periods to 'undefined' on error
                //not showing anything should cause less confusion than showing an incorrect date range
                commit('setPeriodStart', undefined);
                commit('setPeriodEnd', undefined);
                
                throw e;
            } finally {
                commit('setIsLoadingPeriodRange', false);
            }
        })
    }
};